/**
 * CaseDiscoveryPanel — was discovery asked for, did it arrive, and where is it now.
 *
 * Lloyd, Aug 19 2026: "Maybe a tab button for discovery requested and discovery received and
 * location (emailed to Lloyd, mailed thumb drive, waiting to be picked up from court; in DropBox or
 * other places)."
 *
 * Three facts, and the third is the one that costs the firm time. What comes back on these cases is
 * usually dashcam video on physical media, so "we have it" and "it is sitting at the court waiting
 * for somebody to drive over" are entirely different states, and until now neither was written
 * anywhere.
 *
 * The two dates are also what fill the DS and DR boxes on the case journey. Both shipped hollow on
 * Aug 18 with the note "not recorded yet"; this panel is what changes that, which is why saving
 * here hands the updated case back to the page rather than only updating itself — the chart above
 * has to move at the same moment.
 *
 * Nothing here is required and nothing is refused. Staff record what they know when they know it,
 * and anything odd about the result is named underneath rather than blocking the save.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DISCOVERY_LOCATIONS,
  DISCOVERY_LOCATION_LABELS,
  describeDiscoveryGaps,
} from '../../constants/discoveryLocations';

export interface DiscoveryFields {
  Ticket_DiscoveryRequestedAt: string | null;
  Ticket_DiscoveryReceivedAt: string | null;
  Ticket_DiscoveryLocation: string | null;
  Ticket_DiscoveryNote: string | null;
}

interface CaseDiscoveryPanelProps {
  /** Current values from the case. Re-syncs the form whenever the case reloads. */
  value: DiscoveryFields;
  /** Saves the four fields and resolves once the page has the updated case. */
  onSave: (fields: DiscoveryFields) => Promise<void>;
  disabled?: boolean;
}

/** A `date` column arrives as YYYY-MM-DD, but a timestamp would not — trim either to the input. */
function toDateInput(value: string | null): string {
  if (!value) return '';
  return value.length >= 10 ? value.slice(0, 10) : value;
}

function CaseDiscoveryPanel({ value, onSave, disabled = false }: CaseDiscoveryPanelProps) {
  const [requestedAt, setRequestedAt] = useState(toDateInput(value.Ticket_DiscoveryRequestedAt));
  const [receivedAt, setReceivedAt] = useState(toDateInput(value.Ticket_DiscoveryReceivedAt));
  const [location, setLocation] = useState(value.Ticket_DiscoveryLocation ?? '');
  const [note, setNote] = useState(value.Ticket_DiscoveryNote ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // The case is reloaded by the page after every save and after a stage move, so the panel follows
  // the record rather than holding a stale copy of it.
  useEffect(() => {
    setRequestedAt(toDateInput(value.Ticket_DiscoveryRequestedAt));
    setReceivedAt(toDateInput(value.Ticket_DiscoveryReceivedAt));
    setLocation(value.Ticket_DiscoveryLocation ?? '');
    setNote(value.Ticket_DiscoveryNote ?? '');
  }, [
    value.Ticket_DiscoveryRequestedAt,
    value.Ticket_DiscoveryReceivedAt,
    value.Ticket_DiscoveryLocation,
    value.Ticket_DiscoveryNote,
  ]);

  const gaps = describeDiscoveryGaps({
    requestedAt: requestedAt || null,
    receivedAt: receivedAt || null,
    location: location || null,
    note: note || null,
  });

  const dirty =
    requestedAt !== toDateInput(value.Ticket_DiscoveryRequestedAt) ||
    receivedAt !== toDateInput(value.Ticket_DiscoveryReceivedAt) ||
    location !== (value.Ticket_DiscoveryLocation ?? '') ||
    note !== (value.Ticket_DiscoveryNote ?? '');

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await onSave({
        Ticket_DiscoveryRequestedAt: requestedAt || null,
        Ticket_DiscoveryReceivedAt: receivedAt || null,
        Ticket_DiscoveryLocation: location || null,
        Ticket_DiscoveryNote: note.trim() || null,
      });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the discovery record');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Filling in these two dates fills in the DS and DR boxes on the progression chart above.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Discovery requested"
          type="date"
          size="small"
          fullWidth
          disabled={disabled || saving}
          InputLabelProps={{ shrink: true }}
          helperText="When the 39.14 request went to the prosecutor"
          value={requestedAt}
          onChange={(e) => setRequestedAt(e.target.value)}
        />
        <TextField
          label="Discovery received"
          type="date"
          size="small"
          fullWidth
          disabled={disabled || saving}
          InputLabelProps={{ shrink: true }}
          helperText="When it actually came back"
          value={receivedAt}
          onChange={(e) => setReceivedAt(e.target.value)}
        />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FormControl size="small" fullWidth disabled={disabled || saving}>
          <InputLabel id="discovery-location-label" shrink>
            Where it is
          </InputLabel>
          <Select
            labelId="discovery-location-label"
            label="Where it is"
            displayEmpty
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          >
            <MenuItem value="">
              <em>Not recorded</em>
            </MenuItem>
            {DISCOVERY_LOCATIONS.map((code) => (
              <MenuItem key={code} value={code}>
                {DISCOVERY_LOCATION_LABELS[code]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Note"
          size="small"
          fullWidth
          disabled={disabled || saving}
          helperText="Tracking number, who has the drive, the folder it is in"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Stack>

      {gaps.length > 0 && (
        <Alert severity="info" variant="outlined">
          <Stack spacing={0.5}>
            {gaps.map((gap) => (
              <Typography key={gap} variant="body2">
                {gap}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      {error && <Alert severity="error">{error}</Alert>}
      {saved && !dirty && <Alert severity="success">Discovery record saved.</Alert>}

      <Box>
        <Button
          variant="contained"
          size="small"
          disabled={disabled || saving || !dirty}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : 'Save discovery record'}
        </Button>
      </Box>
    </Stack>
  );
}

export default CaseDiscoveryPanel;
